import React, { ReactNode } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { getErrorMessage, getOversizedResponseDetail } from '@/utils/get-error-message';

const { Panel: DialogPanel, Title: DialogTitle } = Dialog;
const { Child: TransitionChild } = Transition;

const confirmColorClasses = {
  blue: 'bg-blue-600 hover:bg-blue-700 focus-visible:ring-blue-500',
  red: 'bg-red-600 hover:bg-red-700 focus-visible:ring-red-500',
  green: 'bg-green-600 hover:bg-green-700 focus-visible:ring-green-500',
} as const;

interface ConfirmDialogProps {
  show: boolean;
  onClose: () => void;
  title: string;
  titleColor?: 'default' | 'red';
  confirmLabel: string;
  confirmColor?: keyof typeof confirmColorClasses;
  isLoading: boolean;
  /**
   * Disable the confirm button — e.g. a required input is still empty (LR-470),
   * or the submit is a no-op in the current state so an enabled button never
   * silently does nothing (LR-478 review r2). Cancel stays enabled. Opt-in: when
   * omitted, behaviour is unchanged.
   */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  error?: Error | null;
  errorFallback?: string;
  /**
   * Opt in on actions whose failure can carry an upstream reason too long for the
   * ordinary error line — the BackOffice posting endpoints (LR-581), where
   * `getErrorMessage` drops a >500-char `detail` to the generic fallback.
   *
   * ONLY such an oversized `detail` gets the verbatim scrollable block; everything
   * shorter keeps the ordinary line. That gate matters because these same dialogs
   * answer routine 409/403/404 with a human sentence in `detail`, and rendering
   * those as a monospace dump was the regression this flag first caused.
   */
  showResponseDetail?: boolean;
  children: ReactNode;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  show,
  onClose,
  title,
  titleColor = 'default',
  confirmLabel,
  confirmColor = 'blue',
  isLoading,
  confirmDisabled = false,
  onConfirm,
  error,
  errorFallback,
  showResponseDetail = false,
  children,
}) => {
  const responseDetail = showResponseDetail ? getOversizedResponseDetail(error) : '';

  return (
    <Transition show={show} as={React.Fragment}>
      <Dialog as="div" className="relative z-[10010]" onClose={() => { if (!isLoading) onClose(); }}>
        <TransitionChild
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                <DialogTitle
                  as="h3"
                  className={`text-lg font-medium leading-6 ${titleColor === 'red' ? 'text-red-600' : 'text-gray-900'}`}
                >
                  {title}
                </DialogTitle>
                <div className="mt-2">
                  {children}
                  {error && (responseDetail ? (
                    <pre
                      tabIndex={0}
                      role="region"
                      aria-label="Error detail"
                      className="mt-4 max-h-32 overflow-y-auto whitespace-pre-wrap break-all rounded border border-red-200 bg-red-50 p-2 text-[11px] font-mono text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                    >
                      {responseDetail}
                    </pre>
                  ) : (
                    <div className="mt-4 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-600">
                      {getErrorMessage(error, errorFallback || 'An error occurred. Please try again.')}
                    </div>
                  ))}
                </div>

                <div className="mt-4 flex justify-end gap-3">
                  <button
                    type="button"
                    disabled={isLoading}
                    className="inline-flex justify-center rounded-md border border-transparent bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2 disabled:opacity-50"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={isLoading || confirmDisabled}
                    className={`inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed min-w-[80px] ${confirmColorClasses[confirmColor]}`}
                    onClick={onConfirm}
                  >
                    {isLoading ? (
                      <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      confirmLabel
                    )}
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
